import { OptimizedPlugin } from '../../core/plugin-interface.js';
import { getAllImages } from '../../utils/dom-utils.js';
/**
 * LazyLoadingPlugin - Implements intelligent lazy loading with native support
 * Implements Patent 2: Selective Content Loading System
 */
export class LazyLoadingPlugin extends OptimizedPlugin {
    constructor(config) {
        super({
            batchSize: 10,
            ...config
        });
        this.name = 'LazyLoadingPlugin';
        this.config = {
            threshold: 0.1,
            rootMargin: '50px',
            preloadDistance: 1.5,
            ...config
        };
        this.supportsNativeLazyLoading = 'loading' in HTMLImageElement.prototype;
        this.viewportHeight = window.innerHeight;
    }
    /**
     * Initialize the plugin
     */
    async init() {
        const startTime = performance.now();
        try {
            // Check if browser supports native lazy loading
            if (!this.supportsNativeLazyLoading) {
                this.log('Browser does not support native lazy loading, skipping plugin', 'warn');
                return;
            }
            // Update viewport height on resize
            window.addEventListener('resize', () => {
                this.viewportHeight = window.innerHeight;
            }, { passive: true });
            this.log('Initialized with native lazy loading support', 'debug');
        }
        finally {
            this.metrics.initTime = performance.now() - startTime;
        }
    }
    /**
     * Execute lazy loading setup
     */
    async execute() {
        const startTime = performance.now();
        try {
            // Skip execution if browser doesn't support native lazy loading
            if (!this.supportsNativeLazyLoading) {
                this.log('Skipping execution - no native lazy loading support', 'debug');
                return;
            }
            // Get target images
            const images = this.getTargetElements();
            if (images.length === 0) {
                this.log('No images to process', 'debug');
                return;
            }
            this.log(`Processing ${images.length} images for lazy loading`, 'debug');
            // Process images in batches
            await this.processBatches(images);
        }
        finally {
            this.metrics.executeTime = performance.now() - startTime;
        }
    }
    /**
     * Read element data for lazy loading decision
     */
    readElementData(img) {
        const rect = img.getBoundingClientRect();
        const isAboveFold = rect.top < this.viewportHeight;
        const isBelowFold = rect.top > this.viewportHeight * this.config.preloadDistance;
        return {
            src: img.src,
            isAboveFold,
            isBelowFold,
            hasNativeLazyLoading: img.loading === 'lazy',
            isLargeImage: this.isLargeImage(img)
        };
    }
    /**
     * Apply lazy loading optimizations to element
     */
    writeElementData(img, data) {
        // Skip if already processed
        if (data.hasNativeLazyLoading) {
            return;
        }
        // Always add decoding="async" for better performance
        img.decoding = 'async';
        // Apply loading strategy based on position
        if (data.isAboveFold) {
            // Above-fold images: eager loading with high priority if large
            img.loading = 'eager';
            if (data.isLargeImage) {
                img.fetchPriority = 'high';
            }
        }
        else {
            // Below-fold images: lazy loading with low priority
            img.loading = 'lazy';
            if (data.isBelowFold) {
                img.fetchPriority = 'low';
            }
        }
        // Set up minimal error handling
        this.setupErrorHandling(img);
    }
    /**
     * Check if image is considered large (likely LCP candidate)
     */
    isLargeImage(img) {
        const rect = img.getBoundingClientRect();
        const imageSize = rect.width * rect.height;
        const viewportSize = window.innerWidth * this.viewportHeight;
        // Image is large if it covers more than 10% of viewport
        return imageSize > viewportSize * 0.1;
    }
    /**
     * Set up minimal error handling
     */
    setupErrorHandling(img) {
        img.addEventListener('error', () => {
            this.log(`Failed to load image: ${img.src}`, 'error');
            // No visual effects - just log the error
            // Browser will show its default broken image indicator
        }, { once: true });
    }
    /**
     * Clean up plugin resources
     */
    async cleanup() {
        await super.cleanup();
        // Restore images to eager loading
        const images = getAllImages();
        images.forEach(img => {
            img.loading = 'eager';
            img.decoding = 'auto'; // Reset to browser default
            if (img.fetchPriority) {
                img.fetchPriority = 'auto';
            }
        });
    }
}
//# sourceMappingURL=index.js.map